import { Select, Upload } from "antd";
import { Button, Form, Input, Toast } from "antd-mobile";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../../../components";
import uploadStatusEnum from "../../../../../enumerations/uploadStatusEnum";
import { useImageUpload } from "../../../../../hooks/imageUpload.hooks";
import agentStatus from "../../enumerations/agentStatus";
import applicationRequiredStatus from "../../enumerations/applicationRequiredStatus";
import { useApplyAgent, useGetAreaCodes } from "../../hooks/apply.hooks";
import styles from "./style.module.scss";
import "./style.scss";

const { Option } = Select;

const ApplicationForm = ({
  requireValidation = applicationRequiredStatus.REQUIRED,
  setShowForm = () => {},
  setCurrentApplicationStatus = () => {},
}) => {
  const { t } = useTranslation();
  const filePath = `imgs/private`;
  const { mutate: uploadReceipt } = useImageUpload();
  const { mutate: applyAgent } = useApplyAgent();

  const { data: areaCodes, isLoading, isError } = useGetAreaCodes({});
  const form = useRef();

  const onHandleSubmitForm = async (values) => {
    const params = {
      ...values,
      area_code: values.mobile?.area_code,
      mobile: values.mobile?.mobile,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === null || params[key] === undefined || params[key] === "") {
        delete params[key];
      }
    });
    await applyAgent(
      {
        params: params,
      },
      {
        onSuccess: (data) => {
          CustomDialog({
            type: "show",
            dialogProps: {
              header: t("apply.success"),
              destroyOnClose: true,
              closeOnAction: true,
              content: t("apply.success.subtitle"),
              actions: [
                {
                  key: "close",
                  text: t("mobile.normal.understand"),
                },
              ],
              onAction: (action, index) => {
                if (action.key === "close") {
                  setCurrentApplicationStatus(agentStatus.PENDING);
                  setShowForm(false);
                }
              },
            },
          });
        },
        onError: (error) => {
          Toast.show({ icon: "fail", content: error });
        },
      },
    );
  };

  const handleCustomRequest = async (options) => {
    const form = new FormData();
    form.append("img", options.file);
    form.append("folder", `${filePath}`);
    await uploadReceipt(
      { params: form },
      {
        onSuccess: (data) => {
          options.onSuccess({
            uid: new Date().valueOf(),
            name: data.filepath.split("/").pop(),
            status: "done",
            url: data.filepath,
          });
        },
        onError: (error) => {
          options.onError(error);
          Toast.show({ icon: "fail", content: error });
        },
      },
    );
  };

  const handleUpdatePath = (event) => {
    const { file } = event;
    if (file.status === uploadStatusEnum.DONE) {
      if (file?.response) {
        const path = file?.response?.url;
        form.current.setFieldValue("identity_img", `${path}`);
        form.current.validateFields();
      }
    }
  };

  const MobileField = ({ value = { area_code: null, mobile: "" }, onChange = () => {} }) => {
    const triggerValue = (changedValue) => {
      onChange?.({ ...value, ...changedValue });
    };

    const onMobileChange = (value) => {
      triggerValue({ mobile: value });
    };

    const onAreaCodeChange = (value) => {
      triggerValue({ area_code: value });
    };

    return (
      <div className={styles.grouped}>
        <Select
          className={styles.areaCode}
          bordered={false}
          placeholder={t("normal.input")}
          options={areaCodes || []}
          value={value.area_code}
          onChange={onAreaCodeChange}
        />
        <Input
          className={`${styles.phone} ${styles.input}`}
          bordered={false}
          value={value.mobile}
          onChange={onMobileChange}
          placeholder={t("mobile.normal.pleaseInput", {
            name: t("personal.info.mobile"),
          })}
        />
      </div>
    );
  };

  const checkMobile = (_, value) => {
    switch (requireValidation) {
      case applicationRequiredStatus.OPTIONAL:
        if (!value) return Promise.resolve();
        if (value.mobile === null && value.area_code === null) return Promise.resolve();
        if (value.mobile && value.area_code) return Promise.resolve();
        break;
      case applicationRequiredStatus.REQUIRED:
        if (value && value.mobile && value.area_code) {
          return Promise.resolve();
        }
        break;
      default:
        break;
    }
    return Promise.reject(new Error(t("form.mobile.required")));
  };

  return (
    <div>
      <Form
        requiredMarkStyle={"none"}
        ref={form}
        onFinish={onHandleSubmitForm}
        layout="horizontal"
        className={`${styles.container} application-form`}
        footer={
          <>
            <div className={styles.notice}>{t("agentApply.dragWarning")}</div>
            <Button type="submit" className={styles.submit}>
              {t("personal.agentList.apply")}
            </Button>
          </>
        }
      >
        <Form.Item
          className={styles.formItem}
          name="real_name"
          rules={[
            {
              required: requireValidation === applicationRequiredStatus.REQUIRED,
              message: t("form.name.required"),
            },
          ]}
          label={t("personal.info.realName")}
        >
          <Input
            className={styles.input}
            placeholder={t("mobile.normal.pleaseInput", {
              name: t("personal.info.realName"),
            })}
          />
        </Form.Item>
        <Form.Item
          className={`${styles.formItem} mobile-item`}
          name="mobile"
          rules={[{ validator: checkMobile }]}
        >
          <MobileField />
        </Form.Item>
        <Form.Item
          className={styles.formItem}
          name="identity_img"
          label={t("agent.apply.img")}
          rules={[
            {
              required: requireValidation === applicationRequiredStatus.REQUIRED,
              message: t("form.id.required"),
            },
          ]}
          extra={
            <Upload
              maxCount={1}
              accept={".png, .jpg, .jpeg"}
              showUploadList={false}
              customRequest={handleCustomRequest}
              onChange={handleUpdatePath}
            >
              <div className={styles.button}>{t("normal.upload")}</div>
            </Upload>
          }
        >
          <Input readOnly />
        </Form.Item>
      </Form>
    </div>
  );
};

export default ApplicationForm;
